/**
 * Pure helpers shared by CardSubEventV3 and ActivityDetailsModal for consistent titles.
 */

export function formatHeureQuebec(raw) {
    if (raw === false || raw === null || raw === undefined) return '';
    const s = String(raw).trim();
    if (!s) return '';
    const compact = s.replace(/\s/g, '');
    const m = compact.match(/^(\d{1,2})(?:[hH:](\d{1,2}))?$/);
    if (!m) return s;
    const hour = parseInt(m[1], 10);
    const min = m[2] !== undefined ? parseInt(m[2], 10) : 0;
    if (
        Number.isNaN(hour) ||
        Number.isNaN(min) ||
        hour < 0 ||
        hour > 23 ||
        min < 0 ||
        min > 59
    ) {
        return s;
    }
    if (min === 0) {
        return `${hour} h`;
    }
    return `${hour} h ${String(min).padStart(2, '0')}`;
}

export function buildLegacyCompositeTitle({
    artiste,
    releve,
    chorale,
    animation,
    animateur,
    representants,
    categorie,
    subCategorie,
    artistNames,
}) {
    const artistStr =
        artistNames && artistNames.length > 0
            ? artistNames.join(', ')
            : artiste
              ? String(artiste)
              : '';
    let setTitre = '';
    let nbEl = 0;
    if (artistStr) {
        setTitre = artistStr;
        nbEl++;
    }
    if (releve) {
        if (nbEl === 1) setTitre += ' - ';
        setTitre += releve;
        nbEl++;
    }
    if (chorale && nbEl < 2) {
        if (nbEl === 1) setTitre += ' - ';
        setTitre += chorale;
        nbEl++;
    }
    if (animation && nbEl < 2) {
        if (nbEl === 1) setTitre += ' - ';
        setTitre += animation;
        nbEl++;
    }
    if (animateur && nbEl < 2) {
        if (nbEl === 1) setTitre += ' - ';
        setTitre += animateur;
        nbEl++;
    }
    if (representants && nbEl < 2) {
        if (nbEl === 1) setTitre += ' - ';
        setTitre += representants;
        nbEl++;
    }
    if (setTitre === '') {
        if (categorie) setTitre = categorie;
        if (subCategorie) setTitre = subCategorie;
        if (categorie && !subCategorie) setTitre = categorie;
    }
    return setTitre;
}

function normalizeArtistNames(artistNamesProp, artiste) {
    if (Array.isArray(artistNamesProp) && artistNamesProp.length > 0) {
        return artistNamesProp.map((name) => String(name).trim()).filter(Boolean);
    }
    if (!artiste) return [];
    return String(artiste)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}

/**
 * @param {object} props — same shape as CardSubEventV3 props (subset used for titles).
 */
export function computeActivityCardText(props) {
    const {
        titre = null,
        animateur = null,
        subCategorie = null,
        artiste = null,
        artistNames: artistNamesProp = null,
        animation = null,
        chorale = null,
        releve = null,
        representants = null,
        categorie = null,
    } = props;

    const namesFromProp = normalizeArtistNames(artistNamesProp, artiste);
    const tTrim = titre && String(titre).trim();
    const animTrim = animateur && String(animateur).trim();
    const subTrim = subCategorie && String(subCategorie).trim();
    const artistCount = namesFromProp.length;

    let mainTitle = '';
    let titleSource = 'fallback';

    if (tTrim) {
        mainTitle = tTrim;
        titleSource = 'activity';
    } else if (artistCount === 1) {
        mainTitle = namesFromProp[0];
        titleSource = 'singleArtist';
    } else if (subTrim) {
        mainTitle = subTrim;
        titleSource = 'subCat';
    } else if (animTrim) {
        mainTitle = animTrim;
        titleSource = 'animateur';
    } else {
        mainTitle = buildLegacyCompositeTitle({
            artiste,
            releve,
            chorale,
            animation,
            animateur,
            representants,
            categorie,
            subCategorie,
            artistNames: namesFromProp,
        });
        titleSource = 'fallback';
    }

    const subtitles = [];
    if (titleSource === 'activity') {
        if (subTrim) {
            subtitles.push(subTrim);
        }
        if (animTrim && animTrim !== tTrim) {
            subtitles.push(`Animé par ${animTrim}`);
        }
        if (artistCount === 1) {
            subtitles.push(`Avec ${namesFromProp[0]}`);
        } else if (artistCount === 2) {
            subtitles.push(`Avec ${namesFromProp[0]} et ${namesFromProp[1]}`);
        } else if (artistCount >= 3) {
            subtitles.push(`${artistCount} artistes`);
        }
    } else if (titleSource === 'subCat') {
        if (artistCount >= 2) {
            subtitles.push(`${artistCount} artistes`);
        }
        if (animTrim && animTrim !== mainTitle) {
            subtitles.push(`Animé par ${animTrim}`);
        }
    } else if (titleSource === 'singleArtist') {
        if (animTrim && animTrim !== mainTitle) {
            subtitles.push(`Animé par ${animTrim}`);
        }
    } else if (titleSource === 'animateur') {
        if (subTrim) {
            subtitles.push(subTrim);
        }
        if (artistCount >= 2) {
            subtitles.push(`${artistCount} artistes`);
        } else if (artistCount === 1 && namesFromProp[0] !== mainTitle) {
            subtitles.push(`Avec ${namesFromProp[0]}`);
        }
    } else if (titleSource === 'fallback') {
        if (artistCount >= 3) {
            subtitles.push(`${artistCount} artistes`);
        } else if (artistCount === 2) {
            subtitles.push(`Avec ${namesFromProp[0]} et ${namesFromProp[1]}`);
        } else if (artistCount === 1 && mainTitle && !mainTitle.includes(namesFromProp[0])) {
            subtitles.push(`Avec ${namesFromProp[0]}`);
        }
    }

    // Do not show categ2 when subTrim is already a subtitle line (e.g. activity + titre,
    // or animateur as main title) — avoids duplicate sous-catégorie on the card.
    const showSubCategorieRow =
        subTrim &&
        subTrim !== mainTitle &&
        String(subTrim) !== String(mainTitle) &&
        !subtitles.includes(subTrim);

    /** One line under the main modal title (Figma: sub-type like « Musique »). */
    let modalSubtitleLine = '';
    if (subTrim && subTrim !== mainTitle) {
        modalSubtitleLine = subTrim;
    }

    return {
        mainTitle,
        titleSource,
        subtitles,
        showSubCategorieRow,
        namesFromProp,
        modalSubtitleLine,
        subTrim,
        animTrim,
    };
}

/** When no rain policy is set in the data, the modal shows this default (maintained). */
const RAIN_NOTE_DEFAULT_MAINTAINED = "* En cas de pluie l'activité est maintenue.";

function ensureRainNotePeriod(text) {
    const t = text.trim();
    if (!t) return t;
    return t.endsWith('.') ? t : `${t}.`;
}

/**
 * Rain / weather line for the activity details modal (optional API text + cancel policy).
 * Reads PAF fields weather_condition / weather_explication, with legacy fallbacks.
 * If nothing is specified, returns the default “maintenue” line.
 */
export function formatRainNoteLine(pivot) {
    if (!pivot || typeof pivot !== 'object') return '';

    const custom =
        pivot.en_cas_de_pluie ??
        pivot.enCasDePluie ??
        pivot.rain_note ??
        pivot.note_pluie;
    if (typeof custom === 'string' && custom.trim()) {
        const t = custom.trim();
        const line = t.startsWith('*') ? t : `* ${t}`;
        return ensureRainNotePeriod(line);
    }

    const condition =
        pivot.weather_condition ??
        pivot.cancel_if_rain ??
        pivot.cancelIfRain;

    const explication = (
        pivot.weather_explication ??
        pivot.cancel_if_rain_extra ??
        pivot.cancelIfRainExtra ??
        ''
    ).trim();

    const status =
        !condition || condition === '0' || condition === 'maintenue'
            ? 'Maintenue'
            : condition;

    if (status === 'Maintenue') {
        return RAIN_NOTE_DEFAULT_MAINTAINED;
    }
    if (status === 'Annulée') {
        return "* En cas de pluie l'activité est annulée.";
    }
    if (status === 'Déplacée') {
        return explication
            ? `* En cas de pluie l'activité est déplacée : ${explication}.`
            : "* En cas de pluie l'activité est déplacée.";
    }
    if (status === 'Reportée') {
        return explication
            ? `* En cas de pluie l'activité est reportée : ${explication}.`
            : "* En cas de pluie l'activité est reportée.";
    }
    return RAIN_NOTE_DEFAULT_MAINTAINED;
}
